import { Injectable, inject, signal } from '@angular/core';
import { findScroller, onScroll } from '../helpers/scroll';
import { PlatformService } from './platform.service';

/**
 * Lets a full-bleed hero tell the shared header to get out of the way.
 *
 * The header lives in the layout and is used by every page, so it must not know
 * anything about the home page. Instead a hero registers its own element here,
 * and this service publishes a single `opacity` value the header binds to.
 * Pages without a hero never register, so the header stays fully opaque —
 * that's the default, and the safe one.
 *
 * `activate()` runs during SSR too, so the home page's server-rendered header is
 * already transparent. Without that there is a visible flash of solid header
 * before hydration.
 */
@Injectable({ providedIn: 'root' })
export class HeroOverlayService {
  private platform = inject(PlatformService);

  /** 0 = fully transparent header, 1 = normal opaque header. */
  readonly opacity = signal(1);

  /**
   * True when a full-bleed hero is on the page. The header is `fixed`, so it
   * takes no space in flow; the layout adds top padding to compensate — except
   * here, where the hero is meant to run underneath it.
   */
  readonly heroActive = signal(false);

  private el?: HTMLElement;
  private detach?: () => void;

  /** Distance over which the header fades back in as the hero scrolls away. */
  private static readonly FADE_PX = 240;

  /** Called by a hero on init — SSR-safe, sets the correct first-paint state. */
  activate() {
    this.heroActive.set(true);
    this.opacity.set(0);
  }

  /** Called by a hero on destroy, and on navigation away. */
  deactivate() {
    this.el = undefined;
    this.heroActive.set(false);
    this.opacity.set(1);
    this.detach?.();
    this.detach = undefined;
  }

  /** Called by a hero once its element exists in the DOM. Browser only. */
  track(el: HTMLElement) {
    if (!this.platform.isBrowser()) return;
    this.el = el;
    // Scroll events do not bubble from elements, and on this site the window
    // is not what scrolls — listening on `window` alone never fired.
    this.detach?.();
    this.detach = onScroll(findScroller(el), this.onScroll);
    this.onScroll();
  }

  private onScroll = () => {
    if (!this.el) return;
    // Transparent for the whole height of the hero, then fades in over the last
    // FADE_PX as its bottom edge passes the top of the viewport.
    const bottom = this.el.getBoundingClientRect().bottom;
    const fade = HeroOverlayService.FADE_PX;
    const next = bottom >= fade ? 0 : bottom <= 0 ? 1 : 1 - bottom / fade;
    if (Math.abs(next - this.opacity()) > 0.01) this.opacity.set(next);
  };
}
